import React from 'react';
import { isChunkLoadError, clearReloadMarker } from '../utils/lazyWithRetry';

/**
 * The app's only error boundary — a render error anywhere below the router used
 * to unmount the whole tree and paint a blank white page (the same class of
 * failure the founder hit on /feedback, #952).
 *
 * It distinguishes the two cases a user can actually act on:
 *   - a stale build (their tab predates a deploy) → "Reload", which fixes it,
 *   - anything else → an honest apology plus a way back to the Launchpad.
 *
 * lazyWithRetry already auto-reloads once for stale chunks; this catches the
 * case where that reload didn't resolve it, so we never loop silently.
 */
export default class RouteErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error) {
        // Best-effort telemetry; a broken analytics import must not re-throw
        // inside the boundary that is supposed to be the safety net.
        try {
            import('../services/analytics').then(({ track, EVENTS }) => {
                track(EVENTS.APP_ERROR_BOUNDARY, {
                    stale_build: isChunkLoadError(error),
                    // No free text (audit D1) — the NAME is the enum; the full
                    // message stays in console/logs.
                    error_kind: String(error?.name || 'Error').slice(0, 40),
                    path: window.location.pathname,
                });
            }).catch(() => {});
        } catch { /* ignore */ }
    }

    render() {
        const { error } = this.state;
        if (!error) return this.props.children;

        const stale = isChunkLoadError(error);
        return (
            <div className="min-h-screen bg-[#FDFCF7] flex items-center justify-center px-4"
                 data-testid="route-error-boundary">
                <div className="max-w-md w-full rounded-2xl border border-[#E0DED8] bg-white px-6 py-8 text-center">
                    <h1 className="font-serif text-xl font-semibold text-[#2C2C2C]">
                        {stale ? 'Fullrun just updated' : 'Something went wrong'}
                    </h1>
                    <p className="mt-2 text-sm text-[#6B6B6B]">
                        {stale
                            ? 'This tab is running an older version. Reload to pick up the latest — your work is saved.'
                            : 'That page failed to load. Reloading usually clears it; if it keeps happening, tell us and we\'ll fix it.'}
                    </p>
                    {/* A reload fixes a stale shell but CANNOT fix a cached bad
                        response for an asset URL (see sw.js). If reloading has
                        not worked, this is the step that does — so say it
                        plainly rather than let someone loop on the button. */}
                    {stale && (
                        <p className="mt-3 text-xs text-[#9A968C]">
                            Still seeing this after reloading? Clear this site&rsquo;s cached data
                            &mdash; in Chrome: <span className="whitespace-nowrap">DevTools &rarr;
                            Application &rarr; Storage &rarr; Clear site data</span>.
                        </p>
                    )}
                    <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
                        <button
                            type="button"
                            onClick={() => { clearReloadMarker(); window.location.reload(); }}
                            className="rounded-full bg-[#1A4D2E] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#2D6B45] transition-colors"
                        >
                            Reload
                        </button>
                        {!stale && (
                            <a
                                href="/feedback"
                                className="rounded-full border border-[#E0DED8] px-5 py-2.5 text-sm font-semibold text-[#1A4D2E] hover:border-[#1A4D2E] transition-colors"
                            >
                                Tell us what happened
                            </a>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}
